import React, { useState } from 'react';
import {
    Box, Typography, Button, TextField, Alert,
    Popover, List, ListItemButton, ListItemText,
    InputAdornment, useTheme, useMediaQuery,
} from '@mui/material';
import { Search } from '@mui/icons-material';

export interface AuditQuickRange {
    label: string;
    ms: number;
}

export const AUDIT_QUICK_RANGES: AuditQuickRange[] = [
    { label: 'Last 1 hour', ms: 60 * 60 * 1000 },
    { label: 'Last 6 hours', ms: 6 * 60 * 60 * 1000 },
    { label: 'Last 24 hours', ms: 24 * 60 * 60 * 1000 },
    { label: 'Last 7 days', ms: 7 * 24 * 60 * 60 * 1000 },
    { label: 'Last 30 days', ms: 30 * 24 * 60 * 60 * 1000 },
    { label: 'Last 90 days', ms: 90 * 24 * 60 * 60 * 1000 },
];

function toLocalDatetimeStr(date: Date) {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function fmtDate(d: Date) {
    return d.toLocaleString('en-GB', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
    });
}

interface AuditTimePickerPopoverProps {
    anchorEl: HTMLElement | null;
    onClose: () => void;
    timeRangeLabel: string;
    onQuickRangeSelect: (range: AuditQuickRange) => void;
    onApplyCustomRange: (fromMs: number, toMs: number, label: string) => void;
    initialFrom: string;
    initialTo: string;
    tzName: string;
    tzString: string;
}

export const AuditTimePickerPopover: React.FC<AuditTimePickerPopoverProps> = React.memo(({
    anchorEl, onClose, timeRangeLabel, onQuickRangeSelect, onApplyCustomRange,
    initialFrom, initialTo, tzName, tzString,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const open = Boolean(anchorEl);
    const [quickSearch, setQuickSearch] = useState('');
    const [customFrom, setCustomFrom] = useState(initialFrom);
    const [customTo, setCustomTo] = useState(initialTo);
    const [rangeError, setRangeError] = useState('');

    React.useEffect(() => {
        if (open) {
            setCustomFrom(initialFrom);
            setCustomTo(initialTo);
            setQuickSearch('');
            setRangeError('');
        }
    }, [open, initialFrom, initialTo]);

    const handleApply = () => {
        if (!customFrom || !customTo) return;
        const fromDate = new Date(customFrom);
        const toDate = new Date(customTo);
        if (toDate.getTime() - fromDate.getTime() <= 0) {
            setRangeError('"To" must be after "From".');
            return;
        }
        setRangeError('');
        onApplyCustomRange(fromDate.getTime(), toDate.getTime(), `${fmtDate(fromDate)} to ${fmtDate(toDate)}`);
    };

    const filteredQuickRanges = AUDIT_QUICK_RANGES.filter(r =>
        r.label.toLowerCase().includes(quickSearch.toLowerCase()),
    );

    const textFieldSx = {
        mt: 0.5,
        '& .MuiOutlinedInput-root': {
            color: 'text.primary',
            '& fieldset': { borderColor: (t: any) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.23)' },
            '&:hover fieldset': { borderColor: (t: any) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' },
            '&.Mui-focused fieldset': { borderColor: 'primary.main' },
        },
        '& .MuiInputBase-input': { color: 'text.primary' },
        '& input::-webkit-calendar-picker-indicator': {
            filter: (t: any) => t.palette.mode === 'dark' ? 'invert(1)' : 'none',
        },
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'left' : 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: isMobile ? 'left' : 'center' }}
            slotProps={{
                paper: {
                    elevation: 8,
                    sx: {
                        borderRadius: 2,
                        width: isMobile ? '92vw' : 560,
                        maxWidth: isMobile ? '92vw' : 560,
                        maxHeight: isMobile ? '80vh' : 480,
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        overflowY: isMobile ? 'auto' : 'hidden',
                        border: '1px solid', borderColor: 'divider',
                        backgroundColor: (t: any) => t.palette.mode === 'dark' ? '#1a1a2e' : '#f5f6f8',
                        color: (t: any) => t.palette.mode === 'dark' ? '#fff' : '#1a1a2e',
                        boxShadow: (t: any) => t.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.45)' : '0 8px 32px rgba(0,0,0,0.15)',
                    },
                },
            }}
        >
            {/* Absolute Time Range */}
            <Box sx={{
                width: isMobile ? '100%' : 260,
                p: 2, display: 'flex', flexDirection: 'column', gap: 1.5,
                borderRight: isMobile ? 'none' : '1px solid',
                borderBottom: isMobile ? '1px solid' : 'none',
                borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'divider',
            }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>Absolute time range</Typography>

                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>From</Typography>
                    <TextField type="datetime-local" size="small" fullWidth value={customFrom} onChange={e => setCustomFrom(e.target.value)} sx={textFieldSx} />
                </Box>
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>To</Typography>
                    <TextField type="datetime-local" size="small" fullWidth value={customTo} onChange={e => setCustomTo(e.target.value)} sx={textFieldSx} />
                </Box>

                <Button variant="contained" color="secondary" size="small" fullWidth onClick={handleApply}
                    sx={{ textTransform: 'none', fontWeight: 700, mt: 0.5, py: 0.75 }}
                >
                    Apply time range
                </Button>

                {rangeError && (
                    <Alert severity="warning" sx={{ py: 0.25, fontSize: '0.72rem' }} onClose={() => setRangeError('')}>
                        {rangeError}
                    </Alert>
                )}

                <Box sx={{ borderTop: '1px solid', borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'divider', pt: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.disabled' }}>
                        Browser Time&nbsp;&nbsp;{tzName}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ color: 'text.disabled' }}>{tzString}</Typography>
                </Box>
            </Box>

            {/* Quick Ranges */}
            <Box sx={{ flex: isMobile ? 'none' : 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 1.5, pb: 1 }}>
                    <TextField size="small" fullWidth placeholder="Search quick ranges" value={quickSearch}
                        onChange={e => setQuickSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment>,
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: 'text.primary',
                                '& fieldset': { borderColor: (t: any) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.23)' },
                                '&:hover fieldset': { borderColor: (t: any) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' },
                                '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                            },
                            '& .MuiInputBase-input::placeholder': { color: 'text.disabled' },
                        }}
                    />
                </Box>
                <List dense sx={{ flex: 1, overflow: 'auto', pt: 0 }}>
                    {filteredQuickRanges.map(range => (
                        <ListItemButton key={range.label} selected={timeRangeLabel === range.label}
                            onClick={() => onQuickRangeSelect(range)}
                            sx={{
                                py: 0.75, color: 'text.primary',
                                '&:hover': { backgroundColor: 'action.hover' },
                                '&.Mui-selected': { backgroundColor: 'action.selected' },
                                '&.Mui-selected:hover': { backgroundColor: 'action.selected' },
                            }}
                        >
                            <ListItemText primary={range.label}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: timeRangeLabel === range.label ? 700 : 400, sx: { color: 'text.primary' } }}
                            />
                        </ListItemButton>
                    ))}
                </List>
            </Box>
        </Popover>
    );
});

AuditTimePickerPopover.displayName = 'AuditTimePickerPopover';
